import React, { useCallback, useEffect, useId, useState } from 'react';
import {
  FaExternalLinkAlt,
  FaCopy,
  FaCheck,
  FaEdit,
  FaArrowRight,
} from 'react-icons/fa';
import { getDeveloperAppSetupConfig } from '../../config/developerAppSetup';
import { API_BASE_URL } from '../../config/constants';
import { useDeveloperAppSetup } from '../../hooks/useDeveloperAppSetup';
import '../../styles/Home.css';

const TITLE_ID = 'developer-app-setup-title';

/**
 * Wizard modal : instructions console développeur puis saisie Client ID / Secret / redirect.
 *
 * @param {Object} props
 * @param {'twitch'|'spotify'} props.provider
 * @param {object|null} [props.existingApp]
 * @param {(app: object) => void} props.onAppCreated
 * @param {() => void} props.onCancel
 */
export default function DeveloperAppSetupModal({
  provider,
  existingApp,
  onAppCreated,
  onCancel,
}) {
  const config = getDeveloperAppSetupConfig(provider);
  const reactId = useId();
  const titleDomId = `${TITLE_ID}-${reactId}`;
  const [sandboxPayload, setSandboxPayload] = useState(null);

  const {
    step,
    setStep,
    formData,
    handleInputChange,
    isSubmitting,
    error,
    copiedField,
    copyToClipboard,
    suggestedRedirectUri,
    isLoadingUri,
    isEditMode,
    goToStep2,
    handleSubmit,
  } = useDeveloperAppSetup({
    existingApp,
    getSuggestedRedirectUri: config.getSuggestedRedirectUri,
    getFallbackSuggestedUri: config.getFallbackSuggestedUri,
    createApp: config.createApp,
    updateApp: config.updateApp,
    onSuccess: onAppCreated,
  });

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/dev/developer-app-sandbox`, {
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data?.enabled && !cancelled) setSandboxPayload(data);
      } catch {
        /* endpoint absent hors sandbox */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const showSandboxCallout = Boolean(
    sandboxPayload && sandboxPayload[provider]
  );

  const fillSandboxCredentials = useCallback(() => {
    const p = sandboxPayload?.[provider];
    if (!p) return;
    handleInputChange('appName', p.appName || '');
    handleInputChange('clientId', p.clientId || '');
    handleInputChange('clientSecret', p.clientSecret || '');
    if (suggestedRedirectUri) {
      handleInputChange('redirectUri', suggestedRedirectUri);
    }
  }, [sandboxPayload, provider, suggestedRedirectUri, handleInputChange]);

  const renderSandboxCallout = () =>
    showSandboxCallout ? (
      <div
        className="developer-app-setup-sandbox-callout"
        data-testid="developer-app-sandbox-callout"
      >
        <p>
          <strong>Mode sandbox (local)</strong> : identifiants factices pour
          enregistrer l&apos;app dans le bot sans passer par la console{' '}
          {provider === 'twitch' ? 'Twitch' : 'Spotify'}. La connexion OAuth
          réelle reste inchangée.
        </p>
        <button
          type="button"
          className="button secondary"
          data-testid="developer-app-sandbox-fill"
          onClick={fillSandboxCredentials}
        >
          Remplir les identifiants sandbox
        </button>
      </div>
    ) : null;

  const { Icon } = config;

  const renderStep1 = () => (
    <div className="setup-step">
      <div className="step-header">
        <div className="step-number">1</div>
        <h3>{config.step1Title}</h3>
      </div>
      <div className="step-content">
        <p>{config.step1Intro}</p>
        {renderSandboxCallout()}
        <ol className="setup-instructions">
          <li>
            Rendez-vous sur{' '}
            <a
              href={config.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={config.linkClassName}
            >
              {config.consoleUrl}
              <FaExternalLinkAlt className="external-icon" />
            </a>
          </li>
          <li>Cliquez sur &quot;{config.consoleActionLabel}&quot;</li>
          <li>
            Remplissez le nom de votre application (ex: &quot;
            {config.nameExamplePlaceholder}&quot;)
          </li>
          <li>Utilisez l&apos;URL de redirection suivante :</li>
        </ol>

        <div className="redirect-uri-box">
          <code>
            {isLoadingUri ? (
              <span className="loading-text">Chargement de l&apos;URI...</span>
            ) : (
              suggestedRedirectUri
            )}
          </code>
          {!isLoadingUri && (
            <button
              type="button"
              className="copy-btn"
              onClick={() =>
                copyToClipboard(suggestedRedirectUri, 'redirectUri')
              }
              title="Copier l'URI de redirection"
            >
              {copiedField === 'redirectUri' ? <FaCheck /> : <FaCopy />}
            </button>
          )}
        </div>

        <div className="step-actions step-actions--wizard">
          <button type="button" className="button secondary" onClick={onCancel}>
            Annuler
          </button>
          <button type="button" className="button primary" onClick={goToStep2}>
            <span className="step-action-btn__text">
              J&apos;ai créé mon application
            </span>
            <FaArrowRight className="step-action-btn__icon" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="setup-step">
      <div className="step-header">
        <div className="step-number">{isEditMode ? <FaEdit /> : '2'}</div>
        <h3>{isEditMode ? config.step2TitleEdit : config.step2TitleCreate}</h3>
      </div>
      <div className="step-content">
        <p>{isEditMode ? config.step2IntroEdit : config.step2IntroCreate}</p>
        {!isEditMode && renderSandboxCallout()}

        <form onSubmit={handleSubmit} className="app-form">
          <div className="form-group">
            <label htmlFor="appName">Nom de l&apos;application</label>
            <input
              type="text"
              id="appName"
              value={formData.appName}
              onChange={(e) => handleInputChange('appName', e.target.value)}
              placeholder={config.nameExamplePlaceholder}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="clientId">Client ID</label>
            <div className="input-with-copy">
              <input
                type="text"
                id="clientId"
                value={formData.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                placeholder="abc123def456..."
                required
              />
              <button
                type="button"
                className="copy-btn small"
                onClick={() => copyToClipboard(formData.clientId, 'clientId')}
                disabled={!formData.clientId}
                title="Copier le Client ID"
              >
                {copiedField === 'clientId' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <small className="field-help">{config.clientIdHelp}</small>
          </div>

          <div className="form-group">
            <label htmlFor="clientSecret">Client Secret</label>
            <div className="input-with-copy">
              <input
                type="password"
                id="clientSecret"
                value={formData.clientSecret}
                onChange={(e) =>
                  handleInputChange('clientSecret', e.target.value)
                }
                placeholder="xyz789..."
                required
              />
              <button
                type="button"
                className="copy-btn small"
                onClick={() =>
                  copyToClipboard(formData.clientSecret, 'clientSecret')
                }
                disabled={!formData.clientSecret}
                title="Copier le Client Secret"
              >
                {copiedField === 'clientSecret' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <small className="field-help">{config.clientSecretHelp}</small>
          </div>

          <div className="form-group">
            <label htmlFor="redirectUri">{config.redirectUriFieldLabel}</label>
            <input
              type="url"
              id="redirectUri"
              value={formData.redirectUri}
              onChange={(e) => handleInputChange('redirectUri', e.target.value)}
              placeholder={suggestedRedirectUri}
              required
            />
            <small className="field-help">{config.redirectUriHelp}</small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="step-actions step-actions--wizard">
            <button
              type="button"
              className="button secondary"
              onClick={isEditMode ? onCancel : () => setStep(1)}
            >
              {isEditMode ? 'Annuler' : 'Retour'}
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={isSubmitting}
            >
              <span className="step-action-btn__text">
                {isSubmitting
                  ? isEditMode
                    ? 'Mise à jour...'
                    : 'Création en cours...'
                  : isEditMode
                    ? 'Enregistrer les modifications'
                    : "Créer l'application"}
              </span>
              {!isSubmitting && (
                <FaArrowRight className="step-action-btn__icon" aria-hidden />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div
      className="developer-app-setup-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleDomId}
    >
      <div
        className={`developer-app-setup-modal ${config.themeModifierClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="setup-header">
          <Icon className={config.iconClassName} />
          <h2 id={titleDomId}>
            {isEditMode ? config.editModalTitle : config.modalTitle}
          </h2>
          <p>{isEditMode ? config.editModalSubtitle : config.modalSubtitle}</p>
        </div>

        <div className="setup-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-dot">1</span>
            <span className="step-label">{config.progressLabel1}</span>
          </div>
          <div className="progress-line" />
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-dot">2</span>
            <span className="step-label">{config.progressLabel2}</span>
          </div>
        </div>

        <div className="setup-body">
          {step === 1 ? renderStep1() : renderStep2()}
        </div>
      </div>
    </div>
  );
}
